import React, { useCallback, useMemo, useState } from 'react';
import PageHeader from '../../components/shell/PageHeader';
import ListScreen from '../../components/list/ListScreen';
import useListState from '../../components/list/useListState';
import Drawer, { DrawerField, DrawerSection } from '../../components/overlays/Drawer';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import Modal from '../../components/overlays/Modal';
import { Field, Select, Textarea } from '../../components/ui/Field';
import { statusLabel } from '../../design/tokens';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import { getSessionCounts, listSessions, agentSessionStatusUpdate } from '../../actions/agentAction';
import { formatDate, formatDateTime, formatTime, phone } from '../../helpers/format';

const VIEWS = [
  { key: 'all', label: 'All' },
  { key: 'today', label: 'Today', attention: true },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'completed', label: 'Completed' },
  { key: 'not_conducted', label: 'Not conducted' },
];

const REASONS = [
  { value: 'partner_no_show', label: 'Partner did not join' },
  { value: 'client_no_show', label: 'Client did not join' },
  { value: 'technical', label: 'Technical failure' },
  { value: 'other', label: 'Other' },
];

export default function SessionList() {
  const { toast } = useOverlays();
  const [record, setRecord] = useState(null);
  const [marking, setMarking] = useState(false);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');

  const list = useListState({
    views: VIEWS,
    fetcher: listSessions,
    countsFetcher: getSessionCounts,
    initialSort: { column: 'when', direction: 'desc' },
  });

  const markNotConducted = useCallback(async () => {
    try {
      await agentSessionStatusUpdate(record.id, {
        status: 'not_conducted',
        reason,
        comment,
      });
      toast('Session marked not conducted', { tone: 'success' });
      setMarking(false);
      setReason('');
      setComment('');
      setRecord(null);
      list.reload();
      list.reloadCounts();
    } catch {
      toast('Could not update the session', { tone: 'error' });
    }
  }, [record, reason, comment, toast, list]);

  const columns = useMemo(
    () => [
      { key: 'service', header: 'Service', width: 230, sortable: true, render: (r) => r.service || '—' },
      {
        key: 'when',
        header: 'When',
        width: 170,
        sortable: true,
        render: (r) => (
          <span>
            <span style={{ display: 'block' }}>{formatDate(r.start_time)}</span>
            <span className="sv-mono" style={{ fontSize: 11.5, color: 'var(--text-subtle)' }}>
              {formatTime(r.start_time)}
            </span>
          </span>
        ),
      },
      { key: 'partner', header: 'Partner', width: 170, sortable: true, render: (r) => r.partner || '—' },
      { key: 'client', header: 'Client', width: 170, sortable: true, render: (r) => r.client || '—' },
      {
        key: 'status',
        header: 'Status',
        width: 150,
        sortable: true,
        // "ordered" reads as "Upcoming" to admins; the tone map already treats it
        // as a review-state, so only the label needs overriding.
        render: (r) => (
          <StatusPill status={r.status}>
            {r.status === 'ordered' ? 'Upcoming' : statusLabel(r.status)}
          </StatusPill>
        ),
      },
      {
        key: 'meeting',
        header: 'Meeting',
        width: 120,
        render: (r) =>
          r.meeting_link && r.status === 'ordered' ? (
            <a href={r.meeting_link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
              Join
            </a>
          ) : (
            '—'
          ),
      },
    ],
    []
  );

  return (
    <>
      <PageHeader
        section="Partners"
        title="Sessions"
        subtitle="Every booked consultation, and what happened to it."
      />

      <ListScreen
        list={list}
        columns={columns}
        rowKey={(r) => r.id}
        noun="sessions"
        selectable={false}
        searchPlaceholder="Partner, client or service"
        onRowClick={setRecord}
        empty={{ icon: 'bx-calendar-event', title: 'No sessions', description: 'Nothing matches this view.' }}
      />

      <Drawer
        open={!!record}
        onClose={() => setRecord(null)}
        title={record?.service || 'Session'}
        status={record?.status === 'ordered' ? 'upcoming' : record?.status}
        footer={
          record?.status === 'ordered' ? (
            <Button variant="danger-text" onClick={() => setMarking(true)}>
              Mark not conducted
            </Button>
          ) : null
        }
      >
        <DrawerSection title="Timing">
          <DrawerField label="Date">{formatDate(record?.start_time)}</DrawerField>
          <DrawerField label="Time" mono>
            {record ? `${formatTime(record.start_time)} – ${formatTime(record.end_time)}` : null}
          </DrawerField>
          <DrawerField label="Timezone">{record?.timezone}</DrawerField>
          <DrawerField label="Booked">{formatDateTime(record?.created_at)}</DrawerField>
        </DrawerSection>

        <DrawerSection title="Participants">
          <DrawerField label="Partner">{record?.partner}</DrawerField>
          <DrawerField label="Partner phone" mono>
            {phone(record?.agent_phone)}
          </DrawerField>
          <DrawerField label="Client">{record?.client}</DrawerField>
          <DrawerField label="Client email">{record?.client_email}</DrawerField>
          <DrawerField label="Client phone" mono>
            {phone(record?.client_phone)}
          </DrawerField>
        </DrawerSection>

        <DrawerSection title="Meeting">
          <DrawerField label="Link">
            {record?.meeting_link ? (
              <a href={record.meeting_link} target="_blank" rel="noopener noreferrer">
                Open
              </a>
            ) : null}
          </DrawerField>
        </DrawerSection>
      </Drawer>

      <Modal
        open={marking}
        onClose={() => setMarking(false)}
        title="Mark session not conducted"
        size="form"
        confirmLabel="Mark not conducted"
        confirmDisabled={!reason}
        destructive
        onConfirm={markNotConducted}
      >
        <p style={{ marginTop: 0 }}>
          This records that the session with {record?.client} did not take place. It affects the
          partner&rsquo;s payout for this period.
        </p>
        <Field label="Reason" required>
          <Select options={REASONS} placeholder="Choose a reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div style={{ height: 12 }} />
        <Field label="Comment">
          <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
      </Modal>
    </>
  );
}
